








































































































































































































































































import { notify } from "@/store";
import { getColor } from "@/lib/identicon";
import { decrypt } from "@/lib/crypto";
import { useExplorer } from "@/hooks/use-explorer";
import {
  computed,
  defineComponent,
  onMounted,
  ref,
  watch
} from "@vue/composition-api";
import { useVuex } from "@/hooks/use-vuex";
import { useRouter } from "@/hooks/use-router";
import { KeyPair } from "@/sdk";
import { useCopy } from "@/hooks/use-copy";
import NetworkList from "@/components/NetworkList.vue";
import { Wallet } from "@/models/wallet";

export default defineComponent({
  components: { NetworkList },
  setup(_, { root }) {
    const { store } = useVuex();
    const wallet = computed(() => store.getters.Wallet.wallet as Wallet);
    const router = useRouter();
    const expert = computed(() => store.state.Settings.mode === "expert");
    const { addressUrl } = useExplorer();
    const qr = ref("");
    const qrPub = ref("");
    const hex = ref("");
    const menu = ref(false);
    const defaultColor = ref(getColor(wallet?.value?.address || ""));
    const copy = useCopy;
    const swatches = computed(() => [
      [defaultColor.value],
      [root.$vuetify.theme.currentTheme.primary],
      [root.$vuetify.theme.currentTheme.error],
      [root.$vuetify.theme.currentTheme.accent],
      [root.$vuetify.theme.currentTheme.success]
    ]);

    const color = computed({
      get: () =>
        wallet?.value?.color ||
        (root.$vuetify.theme.currentTheme.primary as string),
      set: (value: string) => {
        store.commit.Wallet.patchWallet({
          address: wallet?.value?.address || "",
          update: { color: value }
        });
        root.$route.meta.toolbarType = value;
      }
    });

    const panels = ref({
      phrase: false,
      pk: false,
      pubk: false
    });

    const reveal = async (key: string) => {
      if (panels.value[key]) {
        panels.value[key] = false;
        return;
      }
      try {
        const pin = await store.dispatch.Login.promptPin({
          persistent: false
        });
        if (pin) panels.value[key] = true;
      } catch {
        console.warn("Could not get pin");
      }
    };

    const backup = async () => {
      const result = await store.dispatch.Login.promptPin({
        persistent: false
      });
      if (result) router.push("/wallet/backup?redirect=" + root.$route.path);
    };

    const privateKey = computed(() => {
      const pin = store.state.Login.pin;
      if (!pin)
        return "xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx";
      const pair: KeyPair = JSON.parse(decrypt(wallet?.value?.keyPair, pin));
      return pair.secret;
    });

    const publicKey = computed(() => {
      const pin = store.state.Login.pin;
      if (!pin)
        return "xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx";
      const pair: KeyPair = JSON.parse(decrypt(wallet?.value?.keyPair, pin));
      return pair.public;
    });

    const keytoQR = async () => {
      if (!wallet.value) {
        qr.value = "";
        qrPub.value = "";
      } else {
        qr.value = await store.dispatch.getQR(privateKey.value);
        qrPub.value = await store.dispatch.getQR(publicKey.value);
      }
    };

    onMounted(() => {
      const authorizedPanel = root.$route.query.authorizedPanel as string;
      if (authorizedPanel) {
        panels.value[authorizedPanel] = true;
      }
    });

    const downloadKey = (
      key = privateKey.value,
      name = `${wallet?.value?.name}.pk`
    ) => {
      const payload = { value: key, name, encoding: "hex" };
      store.dispatch.downloadString(payload);
    };

    const downloadQR = (
      _qr = qr.value,
      name = `${wallet?.value?.name}.png`
    ) => {
      store.dispatch.downloadURL({ url: _qr, name });
    };

    const mnemonic = computed(() => {
      const pin = store.state.Login.pin;
      if (!pin)
        return "xxxxxx xxxxxx xxxxxx xxxxxx xxxxxx xxxxxx xxxxxx xxxxxx xxxxxx xxxxxx xxxxxx xxxxxx";
      const decrypted = decrypt(wallet?.value?.mnemonic, pin);
      return decrypted;
    });

    const changeName = async () => {
      const name = await root.$dialog.prompt({
        title: "Enter a new name"
      });
      const exists = store.state.Wallet.wallets
        .filter(w => w.address !== wallet.value?.address)
        .map(w => w.name)
        .find(n => n === name);
      if (!name) {
        return;
      } else if (exists) {
        notify({
          text: "A wallet with this name already exists",
          duration: 2000,
          type: "error"
        });
        changeName();
      } else {
        store.commit.Wallet.patchWallet({
          address: wallet.value?.address || "",
          update: { name }
        });
      }
    };

    watch(() => wallet.value, keytoQR, {
      immediate: true,
      deep: true
    });

    const remove = () => {
      if (!wallet.value) return;
      store.dispatch.Wallet.removeWallet(wallet.value);
    };

    return {
      qr,
      qrPub,
      remove,
      hex,
      menu,
      defaultColor,
      expert,
      addressUrl,
      swatches,
      color,
      panels,
      reveal,
      backup,
      privateKey,
      publicKey,
      copy,
      downloadKey,
      downloadQR,
      mnemonic,
      wallet,
      changeName
    };
  }
});
